import {
  Error,
  HourglassBottom,
  PauseCircleFilled,
  Upload,
} from "@mui/icons-material";
import { Chip, IconButton, Tooltip } from "@mui/material";
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { PageContainer } from "../components/Common/PageContainer";
import SectionTable, { NoRowsOverlay } from "../components/Common/SectionTable";
import { DashboardLayout } from "../components/DashboardLayout/DashboardLayout";
import { InternalError } from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import { useAssets } from "../components/Orion/OrionQueries";
import AssetListingHeader from "../components/PageHeader/AssetListing";
import { AssetAttrs } from "../interfaces/api";
import { Status } from "../interfaces/common";

export const AssetsListingPage: React.FC = () => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "updated_date",
      sort: "desc",
    },
  ]);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sortingField, setSortingField] = useState<string | undefined>(
    "updated_date"
  );
  const [sortingOrder, setSortingOrder] = useState<GridSortDirection>("desc");
  const navigate = useNavigate();
  const {
    data: paginatedAssets,
    isLoading: isLoadingAssets,
    isError: isErrorAssets,
  } = useAssets({
    page: pageNumber,
    limit: pageSize,
    sortBy: sortingField,
    sortOrder: sortingOrder,
  });
  const assets = paginatedAssets?.items;

  function getRowId(row: AssetAttrs) {
    return row._id;
  }

  const columns: GridColDef<AssetAttrs>[] = [
    {
      field: "updated_date",
      headerName: "Date",
      minWidth: 200,
      flex: 0.5,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.updated_date),
    },
    {
      field: "name",
      headerName: "Nom",
      minWidth: 200,
      flex: 2,
      renderCell: (params) => (
        <span className="font-bold">{params.row.name}</span>
      ),
    },
    {
      field: "processing.config_char.name",
      headerName: "Configuration",
      sortable: false,
      minWidth: 200, // Ensure minimum width for configuration names
      flex: 2,
      renderCell: (params) => {
        if (params.row.processing?.config_char?.name) {
          return (
            <Link
              to={`/orion/config/${params.row.processing?.config_char?._id}`}
              className="no-underline text-[#4c4fa2] font-semibold"
              onClick={(event) => event.stopPropagation()}
            >
              {params.row.processing.config_char.name}
            </Link>
          );
        }
      },
    },
    {
      field: "processing.status",
      headerAlign: "center",
      align: "center",
      sortable: false,
      headerName: "Statut",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        if (params.row.processing?.status) {
          let status = params.row.processing.status;
          if (status === Status.PENDING) {
            return (
              <Tooltip title="Traitement en attente">
                <PauseCircleFilled
                  color="warning"
                  sx={{ verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          } else if (status === Status.IN_PROGRESS) {
            return (
              <Tooltip title="Traitement en cours">
                <HourglassBottom
                  color="info"
                  sx={{ verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          } else if (status === Status.ERROR) {
            return (
              <Tooltip title="Une erreur est survenue">
                <Error color="error" sx={{ verticalAlign: "middle" }} />
              </Tooltip>
            );
          } else if (params.row.processing.workflow) {
            return (
              <Chip label={params.row.processing.workflow} color={"default"} />
            );
          }
        }
      },
    },
    {
      field: "export",
      headerName: "Exporter",
      headerAlign: "center",
      align: "center",
      sortable: false,
      flex: 1,
      renderCell: () => (
        <IconButton color="primary">
          <Upload />
        </IconButton>
      ),
    },
  ];

  const HeaderComponent = useCallback(
    () =>
      assets && <AssetListingHeader selection={selection} assets={assets} />,
    [selection, assets]
  );
  return (
    <DashboardLayout>
      <PageContainer slots={{ header: HeaderComponent }} className="max-w-full">
        {isErrorAssets ? (
          <InternalError />
        ) : isLoadingAssets ? (
          <Loading />
        ) : (
          <SectionTable<AssetAttrs>
            data={assets ?? []}
            dataGridProps={{
              slots: {
                noRowsOverlay: () => NoRowsOverlay("Aucun dossier"),
              },
              sortModel: sortModel,
              onSortModelChange: (model) => setSortModel(model),
              checkboxSelection: true,
              getRowId: getRowId,
              columns,
              onRowClick: (params) =>
                navigate(`/orion/asset/${params.row._id}`),
            }}
            setSelection={setSelection}
            paginationProps={
              paginatedAssets && {
                totalRows: paginatedAssets.total_count,
                pageNumber,
                setPageNumber,
                pageSize,
                setPageSize,
                sortingField,
                setSortingField,
                sortingOrder,
                setSortingOrder,
              }
            }
          />
        )}
      </PageContainer>
    </DashboardLayout>
  );
};
